import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { deliveryTypes } from "./shippingRates";

export const cartItemSerializer = Serializers.object({
  id: uuidSerializer,
  productId: Serializers.string,
  variantId: Serializers.string,
  quantity: Serializers.integer,
});

export const cartItemWithProductInfoSerializer = Serializers.object({
  id: uuidSerializer,
  productId: Serializers.string,
  variantId: Serializers.string,
  quantity: Serializers.integer,
  productName: Serializers.string.optional(),
  variantName: Serializers.string.optional(),
});

export const cartRewardSerializer = Serializers.object({
  id: uuidSerializer,
  productId: Serializers.string,
  variantId: Serializers.string,
  purchaseId: Serializers.string.nullable(),
  pointsPrice: Serializers.decimal,
  engraving: Serializers.string.nullable(),
});

export const cartRewardWithProductInfoSerializer = Serializers.object({
  id: uuidSerializer,
  productId: Serializers.string,
  variantId: Serializers.string,
  purchaseId: Serializers.string.nullable(),
  pointsPrice: Serializers.decimal,
  engraving: Serializers.string.nullable(),
  productName: Serializers.string.optional(),
  variantName: Serializers.string.optional(),
});

export const cartSerializer = Serializers.object({
  items: cartItemSerializer.array(),
  rewards: cartRewardSerializer.array(),
  discountCode: Serializers.string.nullable(),
});

export const addressInputSerializer = Serializers.object({
  address1: Serializers.string,
  city: Serializers.string,
  country: Serializers.string,
  firstName: Serializers.string,
  lastName: Serializers.string,
  phone: Serializers.string.nullable(),
  province: Serializers.string.nullable(),
  zip: Serializers.string,
  company: Serializers.string.nullable(),
});

const deliveryInputSerializer = Serializers.object({
  deliveryType: Serializers.stringOneOf(...deliveryTypes),
  lockerCode: Serializers.string.nullable(),
});

export const createCheckoutFromCartBodySerializer = Serializers.object({
  email: Serializers.string,
  shippingAddress: addressInputSerializer,
  billingAddress: addressInputSerializer,
  delivery: deliveryInputSerializer,
  taxId: Serializers.string.nullable(),
  posthogDistinctId: Serializers.string.nullable(),
  discountCode: Serializers.string.optional(),
});

export type CartItem = SerializerValue<typeof cartItemSerializer>;
export type CartItemWithProductInfo = SerializerValue<typeof cartItemWithProductInfoSerializer>;
export type CartReward = SerializerValue<typeof cartRewardSerializer>;
export type CartRewardWithProductInfo = SerializerValue<typeof cartRewardWithProductInfoSerializer>;

export const cartWithDetailsSerializer = Serializers.object({
  id: uuidSerializer,
  cookieId: Serializers.string,
  userId: Serializers.string.nullable(),
  orderDraftId: Serializers.string.nullable(),
  discountCode: Serializers.string.nullable(),
  isArchived: Serializers.boolean,
  isFulfilled: Serializers.boolean,
  createdAt: Serializers.datetime,
  updatedAt: Serializers.datetime,
  expirationNotificationSentAt: Serializers.datetime.nullable(),
  items: cartItemSerializer.array(),
  rewards: cartRewardSerializer.array(),
});

export const cartWithDetailsAndProductInfoSerializer = Serializers.object({
  id: uuidSerializer,
  cookieId: Serializers.string,
  userId: Serializers.string.nullable(),
  orderDraftId: Serializers.string.nullable(),
  discountCode: Serializers.string.nullable(),
  isArchived: Serializers.boolean,
  isFulfilled: Serializers.boolean,
  createdAt: Serializers.datetime,
  updatedAt: Serializers.datetime,
  expirationNotificationSentAt: Serializers.datetime.nullable(),
  items: cartItemWithProductInfoSerializer.array(),
  rewards: cartRewardWithProductInfoSerializer.array(),
});

export type CartWithDetails = SerializerValue<typeof cartWithDetailsSerializer>;
export type CartWithDetailsAndProductInfo = SerializerValue<typeof cartWithDetailsAndProductInfoSerializer>;

export const cartActions = {
  getCart: HttpAction({
    path: "/cart",
    method: "get",
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
  cartsQuery: HttpAction({
    path: "/carts",
    method: "get",
    qsSerializer: Serializers.object({
      limit: Serializers.integer,
      offset: Serializers.integer,
      isArchived: Serializers.boolean,
      isFulfilled: Serializers.boolean,
      hasRewards: Serializers.boolean,
      excludeEmpty: Serializers.boolean,
      userId: Serializers.string,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({ items: cartWithDetailsSerializer.array(), count: Serializers.integer }),
  }),
  
  getCartById: HttpAction({
    path: "/cart/:cartId",
    method: "get",
    paramsSerializer: Serializers.object({
      cartId: uuidSerializer,
    }),
    requiresAuth: true as const,
    valueSerializer: cartWithDetailsAndProductInfoSerializer,
  }),
  
  addItemToCart: HttpAction({
    path: "/cart/item",
    method: "post",
    bodySerializer: Serializers.object({
      variantId: Serializers.string,
      productId: Serializers.string,
      quantity: Serializers.integer,
    }),
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
  updateCartItemQuantity: HttpAction({
    path: "/cart/item",
    method: "put",
    bodySerializer: Serializers.object({
      itemId: uuidSerializer,
      quantity: Serializers.integer,
    }),
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
  removeItemFromCart: HttpAction({
    path: "/cart/item",
    method: "delete",
    bodySerializer: Serializers.object({
      itemId: uuidSerializer,
    }),
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
  addRewardToCart: HttpAction({
    path: "/cart/reward",
    method: "post",
    bodySerializer: Serializers.object({
      variantId: Serializers.string,
      productId: Serializers.string,
      rewardId: Serializers.string,
      listingUuid: uuidSerializer,
      engraving: Serializers.string.nullable(),
    }),
    valueSerializer: cartSerializer,
  }),
  removeRewardFromCart: HttpAction({
    path: "/cart/reward",
    method: "delete",
    bodySerializer: Serializers.object({
      rewardId: uuidSerializer,
    }),
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
  createCheckoutFromCart: HttpAction({
    path: "/cart/checkout",
    method: "post",
    bodySerializer: createCheckoutFromCartBodySerializer,
    valueSerializer: Serializers.object({ checkoutUrl: Serializers.string.nullable() }),
    requiresAuth: false as const,
  }),
  cancelReward: HttpAction({
    path: "/cart/reward/cancel",
    method: "post",
    bodySerializer: Serializers.object({
      cartId: uuidSerializer,
      rewardId: uuidSerializer,
    }),
    valueSerializer: cartSerializer,
    requiresAuth: true as const,
  }),
  validateDiscountCode: HttpAction({
    path: "/cart/discount/validate",
    method: "post",
    bodySerializer: Serializers.object({
      code: Serializers.string,
    }),
    valueSerializer: Serializers.object({
      isValid: Serializers.boolean,
      discountLabel: Serializers.string.optional(),
      discountRate: Serializers.float.optional(),
      discountFixedAmount: Serializers.string.optional(),
      discountFixedCurrency: Serializers.string.optional(),
      discountAmount: Serializers.float.optional(),
      lineDiscounts: Serializers
        .object({
          variantId: Serializers.string,
          discountAmount: Serializers.float,
        })
        .array()
        .optional(),
      promotedItems: Serializers
        .object({
          variantId: Serializers.string,
          productId: Serializers.string,
          quantity: Serializers.integer,
        })
        .array()
        .optional(),
      shouldAutoAddPromotedItems: Serializers.boolean.optional(),
      invalidReason: Serializers.string.optional(),
    }),
    requiresAuth: false as const,
  }),
  applyDiscountCode: HttpAction({
    path: "/cart/discount",
    method: "put",
    bodySerializer: Serializers.object({
      code: Serializers.string.nullable(),
    }),
    valueSerializer: cartSerializer,
    requiresAuth: false as const,
  }),
};
